import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Receipt } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { paidAccessApi } from "@/lib/api/paid-access";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { paidResourcePageBreadcrumbs } from "./paid-resource-page-breadcrumbs";
import { usePaidResourceFilterTitle } from "./usePaidResourceFilterTitle";

type PurchaseRow = Awaited<ReturnType<typeof paidAccessApi.listPurchases>>[number];

export default function PaidResourcePurchasesPage() {
  const [searchParams] = useSearchParams();
  const resourceId = searchParams.get("resourceId") ?? undefined;
  const resourceTitle = usePaidResourceFilterTitle(resourceId);
  const [rows, setRows] = useState<PurchaseRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    paidAccessApi
      .listPurchases(resourceId)
      .then(setRows)
      .finally(() => setLoading(false));
  }, [resourceId]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Paid resource purchases"
        description={
          resourceId
            ? "Paid and pending invoices for the selected digital resource."
            : "Digital resource invoices from the patron portal (paid and pending)."
        }
        titleSize="md"
        showSearch={false}
        breadcrumbItems={paidResourcePageBreadcrumbs(
          resourceId ? "Purchases" : "Paid resource purchases",
          resourceId,
          resourceTitle
        )}
      />
      {resourceId && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Filtered by resource.</span>
          <Button variant="link" className="h-auto p-0" asChild>
            <Link to="/catalog/paid-purchases">Show all resources</Link>
          </Button>
          <Button variant="link" className="h-auto p-0" asChild>
            <Link to={`/catalog/digital/${resourceId}`}>Open resource</Link>
          </Button>
        </div>
      )}
      {loading && <LoadingSpinner />}
      {!loading && rows.length === 0 && (
        <Card className="border border-gray-200">
          <CardContent className="p-0">
            <EmptyState
              icon={<Receipt className="h-6 w-6 text-slate-400" />}
              title="No purchase invoices yet"
              description={
                resourceId
                  ? "When patrons pay for this resource, invoices will appear here."
                  : "Paid and pending digital resource invoices from the patron portal will show up here."
              }
            />
          </CardContent>
        </Card>
      )}
      <div className="space-y-2">
        {rows.map((row) => {
          const meta = row.metadata as {
            resourceTitle?: string;
            digitalResourceId?: string;
          } | null;
          const name =
            [row.user.firstName, row.user.lastName].filter(Boolean).join(" ") || row.user.email;
          return (
            <Card key={row.id} className="flex flex-wrap items-center justify-between gap-2 p-4">
              <div>
                <p className="font-medium">{meta?.resourceTitle ?? "Digital resource"}</p>
                <p className="text-sm text-muted-foreground">{name}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge variant={row.status === "paid" ? "default" : "secondary"}>
                  {row.status === "paid" ? "Paid" : "Pending"}
                </Badge>
                <span>
                  {row.currency} {Number(row.amountDue).toLocaleString()}
                </span>
                {row.status === "paid" && (
                  <Link
                    className="text-primary hover:underline"
                    to={`/admin/invoices/${row.id}/receipt`}
                  >
                    Receipt
                  </Link>
                )}
                {meta?.digitalResourceId && !resourceId && (
                  <Link
                    className="text-primary hover:underline"
                    to={`/catalog/digital/${meta.digitalResourceId}`}
                  >
                    Resource
                  </Link>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
